import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const RouterContext = createContext(null);

function readBrowserLocation() {
  return { path: window.location.pathname, search: window.location.search };
}

/**
 * A tiny router using real page addresses (/about, /elements/iron) so search engines can
 * find every page. On the build server it is given the page to render as initialLocation.
 */
export function RouterProvider({ initialLocation, children }) {
  const [location, setLocation] = useState(() => initialLocation ?? readBrowserLocation());

  useEffect(() => {
    const handleBackOrForward = () => setLocation(readBrowserLocation());
    window.addEventListener('popstate', handleBackOrForward);
    return () => window.removeEventListener('popstate', handleBackOrForward);
  }, []);

  const navigate = useCallback((url) => {
    window.history.pushState(null, '', url);
    setLocation(readBrowserLocation());
    window.scrollTo({ top: 0 });
  }, []);

  const value = useMemo(() => ({ ...location, navigate }), [location, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter() {
  const router = useContext(RouterContext);
  if (!router) throw new Error('useRouter must be used inside <RouterProvider>.');
  return router;
}
